import { inject, Injectable } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { RendererParams } from '@xt/questionnaire-cdk';
import {
  RendererError,
  RendererSettings,
  RendererSettingsWithCallbacks,
} from '@xt/questionnaire-cdk';
import { firstValueFrom, Subject } from 'rxjs';
import { AppConfigService } from '@xt/core/app-config';
import { UserNotificationService } from '@xt/core/user-notification';
import { QuestionnaireExportService } from '@xt/questionnaire-sdk';
import { HttpErrorResponse } from '@angular/common/http';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { isRendererParamsWithServiceRequestId } from '@features/questionnaire-renderer/cur-renderer-params';
import { FFRendererSaveEvent, FFRendererSaveStatus } from 'ff-renderer';
import { QuestionnairesService } from '@core/data/questionnaires/questionnaires.service';
import { select, Store } from '@ngrx/store';
import { isUserRoleProviderSelector } from '@core/store/selectors/user-role.selectors';
import { QuestionnaireResponseService } from '@core/data/questionnaires/questionnaire-repsonse/questionnaire-respone.service';
import { NativeClientService } from '@shared/services/native-client.service';
import { QuestionnaireDialogService } from '@features/questionnaire-renderer/questionnaire-dialog.service';

const RENDERER_ERRORS = {
  NOT_FOUND: {
    message: 'app.documents.questionnaires.errorNotFound',
    svg: 'svg/empty-states/questionnaires',
  },
  FORBIDDEN: {
    message: 'app.documents.questionnaires.hintNoAccess',
    svg: 'svg/empty-states/questionnaires',
  },
  SERVER_ERROR: {
    message: 'app.documents.questionnaires.errorRenderingFailed',
    svg: 'svg/empty-states/questionnaires',
  },
} as const;

@Injectable({ providedIn: 'root' })
export class CurQuestionnaireRendererInitService {
  private readonly authService = inject(AuthService);
  private readonly questionnairesService = inject(QuestionnairesService);
  private readonly store = inject(Store);
  private readonly appConfigService = inject(AppConfigService);
  private readonly questionnaireResponseService = inject(
    QuestionnaireResponseService,
  );
  private readonly questionnaireExportService = inject(
    QuestionnaireExportService,
  );
  private readonly userNotificationService = inject(UserNotificationService);
  private readonly nativeClientService = inject(NativeClientService);
  private readonly questionnaireDialogService = inject(
    QuestionnaireDialogService,
  );

  public initQuestionnaireRenderer$(params: RendererParams) {
    const subject = new Subject<RendererSettings>();
    this.initQuestionnaireRendererSettings(params, subject);
    return subject.asObservable();
  }

  private async initQuestionnaireRendererSettings(
    params: RendererParams,
    subject: Subject<RendererSettings>,
  ): Promise<void> {
    subject.next({
      name: '...',
      loading: true,
      error: undefined,
    });

    if (!params.questionnaireId) {
      subject.next({
        name: 'N/A',
        loading: false,
        error: RENDERER_ERRORS.NOT_FOUND,
      });
      subject.complete();
      return;
    }
    const isProvider = await this.isProvider();
    const hasReadonlyView = params.readonlyView !== undefined;
    const readonlyViewValue = hasReadonlyView
      ? coerceBooleanProperty(params.readonlyView)
      : false;

    try {
      const settings = {
        ...this.getApiSettings(),
        questionnaireId: params.questionnaireId,
        questionnaireVersion: params.version ?? params.version ?? '1',
        onInit: this.rendererOnInit.bind(this, subject),
        readOnly: isProvider || readonlyViewValue,
        ...(await this.initiateByServiceRequest(subject, params)),
      } as RendererSettingsWithCallbacks;

      subject.next({
        name: params.name,
        loading: false,
        ...settings,
        error: undefined,
      });
    } catch (e) {
      this.emitRendererError(e as Error, subject, params.name);
      return;
    }
  }

  private getApiSettings() {
    // Get authentication tokens based on client type

    const authTokens = this.nativeClientService.isNative
      ? { token: this.nativeClientService.headerValues }
      : { xCsrfToken: this.authService.getCsrfToken() };
    const apiUrl = `${
      this.appConfigService.getConfig().bff
    }/app/forms/freiform/`;

    // TODO
    const languageCode = 'de';

    return {
      apiUrl,
      languageCode,
      ...authTokens,
      themes: ['x-tention'],
    };
  }

  private async getReportId(questionnaireResponseId: string) {
    return await firstValueFrom(
      this.questionnaireExportService.getReportIds$(questionnaireResponseId),
    ).then((ids) => ids[0] || undefined);
  }

  private notifyUser($event: FFRendererSaveEvent | { error: Error }) {
    if ('error' in $event) {
      this.userNotificationService.error(
        'app.documents.questionnaires.saveError',
      );
      return;
    }
    if ($event.status === FFRendererSaveStatus.Completed) {
      this.userNotificationService.success(
        'app.documents.questionnaires.completeSuccess',
      );
    } else {
      this.userNotificationService.success(
        'app.documents.questionnaires.saveSuccess',
      );
    }
  }

  private async rendererOnInit(
    subject: Subject<RendererSettings>,
    $event: { error?: Error },
  ) {
    if ($event.error) {
      this.emitRendererError($event.error, subject);
    }

    subject.complete();
  }

  private handleRendererError(error: Error): RendererError {
    const ffError = (error as any).cause as Error | undefined;
    const httpError = error instanceof HttpErrorResponse ? error : undefined;

    switch (true) {
      case ffError?.message.includes('404 Not Found'):
      case httpError?.status === 404:
        return { ...RENDERER_ERRORS.NOT_FOUND, error };
      case ffError?.message.includes('403 Forbidden'):
      case httpError?.status === 403:
        return { ...RENDERER_ERRORS.FORBIDDEN, error };
      default:
        return { ...RENDERER_ERRORS.SERVER_ERROR, error };
    }
  }

  private async initiateByServiceRequest(
    subject: Subject<RendererSettings>,
    params: RendererParams,
  ): Promise<Partial<RendererSettingsWithCallbacks>> {
    if (!isRendererParamsWithServiceRequestId(params)) {
      return {};
    }

    const questionnaireResponse = await firstValueFrom(
      this.questionnaireResponseService.searchItems$({
        'based-on': `ServiceRequest/${params.serviceRequestId}`,
      }),
    );
    const questionnaireResponseId = questionnaireResponse[0]?.id;

    return {
      // set readonly if response is completed, otherwise to not override setting
      ...(questionnaireResponse[0]?.status === 'completed'
        ? { readOnly: true }
        : {}),
      responseStatusCompleted: questionnaireResponse[0]?.status === 'completed',
      reportId: questionnaireResponseId
        ? await this.getReportId(questionnaireResponseId)
        : undefined,
      questionnaireResponseId: questionnaireResponse[0]?.id,
      onSave: this.onSave.bind(
        this,
        params.serviceRequestId,
        params.questionnaireId,
      ),
    };
  }

  private async onSave(
    serviceRequestId: string,
    questionnaireId: string,
    $event: FFRendererSaveEvent | { error: Error },
  ): Promise<void> {
    if ('error' in $event) return;

    const data = {
      questionnaireResponseId: $event.id,
      serviceRequestId,
      status:
        $event.status === FFRendererSaveStatus.Completed
          ? 'completed'
          : 'draft',
    };

    await firstValueFrom(
      this.questionnairesService.saveQuestionnaireResponseAndServiceRequest$(
        data,
      ),
    );

    this.notifyUser($event);

    if ($event.status === FFRendererSaveStatus.Completed) {
      this.questionnaireDialogService.openShareDialog({
        questionnaireResponseId: $event.id,
        serviceRequestId,
        questionnaireId,
        status:
          $event.status === FFRendererSaveStatus.Completed
            ? 'completed'
            : 'draft',
      });
    }
  }

  public emitRendererError(
    error: Error,
    subject: Subject<RendererSettings>,
    name?: string,
  ): void {
    try {
      subject.next({
        name: name ?? 'N/A',
        loading: false,
        error: this.handleRendererError(error),
      });
      subject.complete();
    } catch (e) {
      subject.error(e);
    }
  }

  private isProvider() {
    return firstValueFrom(this.store.pipe(select(isUserRoleProviderSelector)));
  }
}
